import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth, isSuperAdmin, type ClientRegisterData } from '@/hooks/useAuth';
import { useTenant } from '@/hooks/useTenant';
import { resolveStoreName } from '@/lib/display';
import { validateUsername } from '@/lib/username';
import { useUsernameAvailability } from '@/hooks/useUsernameAvailability';
import AntiBotGuard from '@/components/feature/AntiBotGuard';

// ─────────────────────────────────────────────────────────────
// Connexion & inscription CLIENT sur la boutique / marketplace.
//
// Ces pages s'affichent DANS le site public du tenant (sous-domaine ou
// domaine personnalisé) : le client ne quitte jamais la marketplace — il
// n'est plus redirigé vers le site principal zifek.fr.
//
// Après connexion, on ouvre l'espace client universel Zifek (/mon-compte),
// mais TOUJOURS sur le même domaine (le sous-domaine du commerce).
// ─────────────────────────────────────────────────────────────

const inputClass =
  'w-full h-11 px-4 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-accent-300 focus:ring-1 focus:ring-accent-300 transition-colors';

/** Ouvre l'espace client universel en RESTANT sur le domaine courant. */
function goToAccount(redirect?: string | null) {
  // Retour à la page d'origine (ex. /checkout après « Connectez-vous »).
  // On n'accepte qu'un chemin interne : jamais une URL externe.
  const target = typeof redirect === 'string' && redirect.startsWith('/') && !redirect.startsWith('//')
    ? redirect
    : '/mon-compte';
  window.location.assign(target);
}

export function TenantLoginClientPage() {
  const { user, login, loading } = useAuth();
  const { tenant, theme } = useTenant();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect') || '';
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const storeName = resolveStoreName(tenant, theme);

  useEffect(() => {
    document.title = `Connexion · ${storeName}`;
  }, [storeName]);

  // Déjà connecté → espace client (toujours sur le même domaine).
  useEffect(() => {
    if (!user) return;
    if (isSuperAdmin(user.typecompte)) navigate('/superadmin', { replace: true });
    else goToAccount(redirectTo);
  }, [user, navigate, redirectTo]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    if (!identifier || !password) {
      setError('Veuillez remplir tous les champs.');
      return;
    }
    const result = await login(identifier, password);
    if (result.success && result.user) {
      if (isSuperAdmin(result.user.typecompte)) navigate('/superadmin');
      else goToAccount(redirectTo);
    } else {
      setError(result.error || 'Erreur de connexion.');
    }
  };

  return (
    <div className="w-full max-w-md mx-auto px-4 py-12 md:py-16">
      <div className="text-center mb-8">
        <div className="w-14 h-14 rounded-2xl bg-accent-100 flex items-center justify-center mx-auto mb-4">
          <i className="ri-user-smile-line text-2xl text-accent-600"></i>
        </div>
        <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-2">Bienvenue sur {storeName}</h1>
        <p className="text-foreground-500 text-sm">Connectez-vous à votre compte client</p>
      </div>

      <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 md:p-8">
        {error && (
          <div className="mb-5 p-3 rounded-md bg-red-50/80 border border-red-100 text-red-700 text-sm flex items-start gap-2">
            <i className="ri-error-warning-line flex-shrink-0 mt-0.5"></i>
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="identifier" className="block text-sm font-medium text-foreground-700 mb-1.5">Email ou nom d&apos;utilisateur</label>
            <input id="identifier" type="text" value={identifier} onChange={(e) => setIdentifier(e.target.value)} placeholder="vous@exemple.com" required className={inputClass} />
          </div>
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-foreground-700 mb-1.5">Mot de passe</label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Votre mot de passe"
                required
                className={`${inputClass} pr-10`}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-foreground-600 cursor-pointer"
                aria-label={showPassword ? 'Cacher' : 'Afficher'}
              >
                <i className={`text-lg ${showPassword ? 'ri-eye-off-line' : 'ri-eye-line'}`}></i>
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full h-11 bg-primary-500 text-background-50 rounded-full text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {loading ? (<><i className="ri-loader-4-line animate-spin"></i>Connexion...</>) : 'Se connecter'}
          </button>
        </form>

        <div className="mt-6 pt-5 border-t border-background-200/70 text-center">
          <p className="text-sm text-foreground-500">
            Pas encore de compte ?{' '}
            <Link to="/register-client" className="text-accent-600 font-semibold hover:text-accent-700 transition-colors">
              Créer un compte client
            </Link>
          </p>
          <p className="text-xs text-foreground-400 mt-3">
            Vous êtes un professionnel ?{' '}
            <Link to="/vendeur/inscription" className="text-primary-600 font-medium hover:text-primary-700 transition-colors">
              Devenir vendeur
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export function TenantRegisterClientPage() {
  const { user, registerClient, loading } = useAuth();
  const { tenant, theme } = useTenant();
  const [form, setForm] = useState({ user_name: '', email: '', password: '' });
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [humanOk, setHumanOk] = useState(false);

  const storeName = resolveStoreName(tenant, theme);
  const usernameValidation = validateUsername(form.user_name);
  const { status: usernameStatus, message: usernameStatusMessage } = useUsernameAvailability(form.user_name);

  useEffect(() => {
    document.title = `Créer un compte · ${storeName}`;
  }, [storeName]);

  useEffect(() => {
    if (user) goToAccount();
  }, [user]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    if (!form.user_name || !form.email || !form.password) {
      setError('Veuillez remplir tous les champs.');
      return;
    }
    if (!usernameValidation.valid) {
      setError(usernameValidation.error || 'Nom d\u2019utilisateur invalide.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      setError('Veuillez saisir une adresse email valide.');
      return;
    }
    if (form.password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères.');
      return;
    }
    if (!humanOk) {
      setError('Veuillez compléter la vérification anti-robot.');
      return;
    }
    const data: ClientRegisterData = {
      name: form.user_name,
      email: form.email.trim(),
      user_name: form.user_name,
      password: form.password,
      telephone: '',
    };
    const result = await registerClient(data);
    if (result.success) goToAccount();
    else setError(result.error || 'Erreur lors de l\u2019inscription.');
  };

  return (
    <div className="w-full max-w-md mx-auto px-4 py-12 md:py-16">
      <div className="text-center mb-8">
        <div className="w-14 h-14 rounded-2xl bg-accent-100 flex items-center justify-center mx-auto mb-4">
          <i className="ri-user-add-line text-2xl text-accent-600"></i>
        </div>
        <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-2">Créer votre compte</h1>
        <p className="text-foreground-500 text-sm">Un compte pour commander et réserver sur {storeName}</p>
      </div>

      <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 md:p-8">
        {error && (
          <div className="mb-5 p-3 rounded-md bg-red-50/80 border border-red-100 text-red-700 text-sm flex items-start gap-2">
            <i className="ri-error-warning-line flex-shrink-0 mt-0.5"></i>
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="user_name" className="block text-sm font-medium text-foreground-700 mb-1.5">Nom d&apos;utilisateur <span className="text-red-400">*</span></label>
            <input
              id="user_name"
              type="text"
              value={form.user_name}
              onChange={(e) => setForm((f) => ({ ...f, user_name: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') }))}
              placeholder="nomutilisateur"
              required
              className={inputClass}
            />
            {form.user_name && !usernameValidation.valid && (
              <p className="text-xs text-red-500 mt-1.5 flex items-center gap-1"><i className="ri-error-warning-line"></i>{usernameValidation.error}</p>
            )}
            {form.user_name && usernameValidation.valid && usernameStatus === 'checking' && (
              <p className="text-xs text-foreground-400 mt-1.5 flex items-center gap-1"><i className="ri-loader-4-line animate-spin"></i>Vérification…</p>
            )}
            {form.user_name && usernameValidation.valid && usernameStatus === 'taken' && (
              <p className="text-xs text-red-500 mt-1.5 flex items-center gap-1"><i className="ri-close-circle-line"></i>{usernameStatusMessage || 'Ce nom est déjà pris.'}</p>
            )}
            {form.user_name && usernameValidation.valid && usernameStatus === 'available' && (
              <p className="text-xs text-emerald-600 mt-1.5 flex items-center gap-1"><i className="ri-checkbox-circle-line"></i>Ce nom est disponible</p>
            )}
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">Email <span className="text-red-400">*</span></label>
            <input id="email" type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="vous@gmail.com" required className={inputClass} />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-foreground-700 mb-1.5">Mot de passe <span className="text-red-400">*</span></label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                placeholder="6 caractères minimum"
                required
                className={`${inputClass} pr-10`}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-foreground-600 cursor-pointer"
                aria-label={showPassword ? 'Cacher' : 'Afficher'}
              >
                <i className={`text-lg ${showPassword ? 'ri-eye-off-line' : 'ri-eye-line'}`}></i>
              </button>
            </div>
          </div>

          <AntiBotGuard onValidityChange={setHumanOk} />

          <button
            type="submit"
            disabled={loading}
            className="w-full h-11 bg-primary-500 text-background-50 rounded-full text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-60 flex items-center justify-center gap-2 mt-2"
          >
            {loading ? (<><i className="ri-loader-4-line animate-spin"></i>Création...</>) : 'Créer mon compte'}
          </button>
        </form>

        <div className="mt-6 pt-5 border-t border-background-200/70 text-center">
          <p className="text-sm text-foreground-500">
            Déjà un compte ?{' '}
            <Link to="/login-client" className="text-accent-600 font-semibold hover:text-accent-700 transition-colors">Se connecter</Link>
          </p>
          <p className="text-xs text-foreground-400 mt-3">
            Vous êtes un professionnel ?{' '}
            <Link to="/vendeur/inscription" className="text-primary-600 font-medium hover:text-primary-700 transition-colors">Devenir vendeur</Link>
          </p>
        </div>
      </div>
    </div>
  );
}